/**
 * LaserPointer
 *
 * A small red glowing dot that follows the mouse while the presenter
 * has toggled laser-pointer mode in `PresentationMode`.  Rendered at
 * `position: fixed` so coordinates come straight from the mouse event
 * without having to translate through the slide container's bounding
 * rect.
 *
 * The component is intentionally self-contained:
 *   - Installs a `window.addEventListener("mousemove", ...)` only while
 *     `active` is true.  The listener is torn down when `active` flips
 *     back to false, so the dot stops moving and disappears.
 *   - Starts hidden (null coords) and only becomes visible after the
 *     first mousemove while active — this avoids rendering a dot at
 *     (0, 0) before the presenter has actually moved the mouse.
 *
 * The red color / glow is part of the feature spec (it's a "laser"),
 * so we use inline styles with a fixed rgb + box-shadow rather than
 * introducing a bespoke design-system token for this single visual.
 */
import { useEffect, useState } from "react"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface LaserPointerProps {
  active: boolean
}

interface Position {
  x: number
  y: number
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function LaserPointer({ active }: LaserPointerProps) {
  const [pos, setPos] = useState<Position | null>(null)

  useEffect(() => {
    if (!active) {
      // Reset position so re-enabling the laser starts "off-screen" until
      // the presenter moves the mouse again.
      setPos(null)
      return
    }
    const onMove = (e: MouseEvent) => {
      setPos({ x: e.clientX, y: e.clientY })
    }
    window.addEventListener("mousemove", onMove)
    return () => window.removeEventListener("mousemove", onMove)
  }, [active])

  if (!active || !pos) return null

  // Center the dot on the cursor. We express position via `transform:
  // translate3d(...)` with a `-50%` offset so the dot's visual center
  // lands exactly on (clientX, clientY). Anchoring at top-left (0,0)
  // keeps `translate(-50%, -50%)` symmetric and avoids subpixel drift
  // that accumulates when `left`/`top` and `transform` share duty.
  // `translate3d` also nudges the browser onto the GPU for smoother
  // tracking during fast mouse movement.
  return (
    <div
      aria-hidden="true"
      data-testid="presentation-laser-pointer"
      style={{
        backgroundColor: "rgb(255, 0, 0)",
        borderRadius: "9999px",
        boxShadow: "0 0 12px 4px rgba(255, 0, 0, 0.6)",
        height: 16,
        left: 0,
        margin: 0,
        padding: 0,
        pointerEvents: "none",
        position: "fixed",
        top: 0,
        transform: `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`,
        width: 16,
        willChange: "transform",
        zIndex: 10000,
      }}
    />
  )
}
